import React from "react";
import { Box, Card, CardContent, Skeleton, Stack } from "@mui/material";

/**
 * Reusable skeleton shapes for the eshop. Each skeleton matches the layout
 * of the real component it stands in for so the page doesn't visibly reflow
 * when data lands.
 */

export const ProductCardSkeleton = () => (
  <Card sx={{ height: "100%" }}>
    <Skeleton variant="rectangular" height={200} animation="wave" />
    <CardContent>
      <Skeleton variant="text" width="40%" height={14} />
      <Skeleton variant="text" width="90%" height={22} />
      <Skeleton variant="text" width="60%" height={22} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Skeleton variant="text" width={60} height={28} />
        <Skeleton variant="rounded" width={44} height={44} />
      </Stack>
    </CardContent>
  </Card>
);

export const ProductGridSkeleton = ({ count = 8 }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(4, 1fr)",
      },
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </Box>
);

export const TableRowSkeleton = ({ columns = 5 }) => (
  <Stack
    direction="row"
    spacing={2}
    sx={{
      py: 1.5,
      px: 2,
      borderBottom: (t) => `1px solid ${t.palette.divider}`,
      alignItems: "center",
    }}
  >
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        height={18}
        sx={{ flex: i === 0 ? 2 : 1, minWidth: 60 }}
      />
    ))}
  </Stack>
);

export const ListSkeleton = ({ rows = 6, columns = 5 }) => (
  <Card>
    <Box
      sx={{
        py: 1.25,
        px: 2,
        bgcolor: "background.default",
        borderBottom: (t) => `2px solid ${t.palette.divider}`,
      }}
    >
      <Stack direction="row" spacing={2}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            height={14}
            sx={{ flex: i === 0 ? 2 : 1, minWidth: 60 }}
          />
        ))}
      </Stack>
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={i} columns={columns} />
    ))}
  </Card>
);

export const BasketItemSkeleton = () => (
  <Card sx={{ p: 2 }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Skeleton variant="rounded" width={72} height={72} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="70%" height={22} />
        <Skeleton variant="text" width="40%" height={16} />
      </Box>
      <Skeleton variant="rounded" width={108} height={36} />
      <Skeleton variant="rounded" width={36} height={36} />
    </Stack>
  </Card>
);

export const BasketSkeleton = ({ count = 3 }) => (
  <Stack spacing={1.5}>
    {Array.from({ length: count }).map((_, i) => (
      <BasketItemSkeleton key={i} />
    ))}
  </Stack>
);

export const ProfileSkeleton = () => (
  <Card sx={{ p: 4 }}>
    <Stack spacing={2}>
      <Skeleton variant="text" width={200} height={28} />
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i}>
            <Skeleton variant="text" width={120} height={16} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        ))}
      </Box>
    </Stack>
  </Card>
);

export const ItemDetailSkeleton = () => (
  <Box
    sx={{
      display: "grid",
      gap: 4,
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    }}
  >
    <Skeleton variant="rounded" height={400} />
    <Stack spacing={2}>
      <Skeleton variant="text" width="40%" height={16} />
      <Skeleton variant="text" width="80%" height={32} />
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="95%" height={16} />
      <Skeleton variant="text" width="40%" height={32} sx={{ mt: 2 }} />
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Skeleton variant="rounded" width={80} height={48} />
        <Skeleton variant="rounded" width={160} height={48} />
      </Stack>
    </Stack>
  </Box>
);
